import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailDomainSpf } from '../types/models/MailDomainSpf';
import type { ApiResponseMailDomainSpf } from '../types/api/ApiResponseMailDomainSpf';

interface UseMailDomainSpfReturn {
  data: MailDomainSpf | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Reads the apex SPF policy published on the mail domain's managed DNS zone.
 *
 * The backend performs a best-effort live PowerDNS read, so this is an expensive call — only invoke it
 * from a tab mounted lazily via `mountOnEnter`, never at page level. Per the API contract the response is
 * JSend wrapped and controller exceptions map to `status: 'error'` at HTTP 200, so we branch on
 * `body.status === 'success'` (+ the `spf` key), not `response.ok`. Note that a DNS read failure is NOT a
 * request failure: it returns `status: 'success'` with `spf_state: 'unknown'` and a safe `spf_error` — the
 * tab surfaces that, this hook only sets `error` for an actual transport/contract failure. Previous data is
 * kept during a refetch so the view stays visible while the Refresh button shows its loading state.
 */
export const useMailDomainSpf = (mailDomainId: number | undefined): UseMailDomainSpfReturn => {
  const [data, setData] = useState<MailDomainSpf | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchSpf = useCallback(async () => {
    if (!mailDomainId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/domains/${mailDomainId}/spf`);
      const body = (await response.json()) as ApiResponseMailDomainSpf;

      if (body.status === 'success' && body.data?.spf) {
        setData(body.data.spf);
      } else {
        setError(body.message ?? 'Failed to load SPF record');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading the SPF record');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, mailDomainId]);

  useEffect(() => {
    if (auth.isAuthenticated && mailDomainId) {
      fetchSpf();
    }
  }, [auth.isAuthenticated, mailDomainId, fetchSpf]);

  return { data, loading, error, refetch: fetchSpf };
};
